import { AwsAiService, VertexAiService } from "@/domains/@bot/chat/services";
import { PragyaAiService } from "./services";
import { groupChatModelsByProvider } from "@/shared/utils/groupChatModelsByProvider";
import {
  mapFoldersToCourses,
  handleError,
  processResources,
} from "./pragyaai.helpers";

// --------------- provider-specific response handlers ---------------

const providerHandlers = {
  async aws(model, prompt, selected, sessionId) {
    const req = {
      model,
      prompt,
      action: selected.action,
      course: selected.course,
      adminPrompt: selected.systemInstructions,
    };
    if (sessionId) req.sessionId = sessionId;

    const response = await AwsAiService.getAiResponse(req);
    const data = response.data?.body;

    if (response.data.error) throw new Error(response.data.error);

    let message;
    if (response.data.statusCode !== 200) {
      message = data.generated_text;
    } else if (selected.action === "retrieve") {
      message = data.texts.flat().join("\n\n");
    } else if (selected.action === "retrieve_and_generate") {
      message = data.generated_text;
    } else {
      message = "Error in generating response.";
    }

    return {
      model,
      message,
      sessionId: data.session_id,
      resources: processResources(data.resources || [], "aws"),
    };
  },

  async pragya(model, prompt, selected, sessionId) {
    const req = { model, question: prompt, course: selected.course };
    if (sessionId) req.sessionId = sessionId;

    const response = await PragyaAiService.getAiResponse(req);
    if (response.data.error) throw new Error(response.data.error);

    return {
      model,
      message: response.data.answer,
      resources: [],
    };
  },

  async vertex(model, prompt, selected, sessionId) {
    const filters = Object.assign(
      {},
      ...selected.filters.map((f) => ({ [f.name]: f.value })),
    );

    const req = {
      question: prompt,
      model,
      systemInstruction: selected.systemInstructions,
      ...filters,
    };
    if (sessionId) req.sessionId = sessionId;

    const response = await VertexAiService.getAiResponse(req);

    return {
      model: model.name || model.label,
      message: response.data?.answer || response.data?.message || "",
      sessionId: response.data?.sessionId,
      resources: processResources(response.data.references || [], "vertex"),
    };
  },
};

// --------------- state factory ---------------

const createDefaultState = () => ({
  activeTab: "chat",

  chat: {
    messages: [],
    sessionIds: {},
    responseProvider:
      (typeof localStorage !== "undefined" &&
        localStorage.getItem("responseProvider")) ||
      "aws",
    isGeneratingResponse: false,
    isFetchingOptions: false,
    error: null,
  },

  models: {},

  providers: {
    aws: {
      options: {
        actions: [
          { value: "retrieve_and_generate", name: "Retrieve and Generate" },
          { value: "retrieve", name: "Retrieve" },
        ],
        models: [
          { id: "anthropic.claude-3-haiku-20240307-v1:0", name: "Claude 3 Haiku", parent: "Anthropic", provider: "aws" },
          { id: "anthropic.claude-3-sonnet-20240229-v1:0", name: "Claude 3 Sonnet", parent: "Anthropic", provider: "aws" },
          { id: "meta.llama3-8b-instruct-v1:0", name: "Llama 3 8B Instruct", parent: "Meta", provider: "aws" },
          { id: "meta.llama3-70b-instruct-v1:0", name: "Llama 3 70B Instruct", parent: "Meta", provider: "aws" },
        ],
        courses: [],
      },
      selected: {
        action: "retrieve_and_generate",
        models: [],
        course: "",
        systemInstructions: "",
      },
    },

    pragya: {
      options: {
        models: [],
        modes: [
          { value: "s3", name: "S3" },
          { value: "courseSection", name: "Course Section" },
        ],
        courses: [],
        courseSection: [],
      },
      selected: {
        models: [],
        mode: { value: "s3", name: "S3" },
        course: "",
      },
    },

    vertex: {
      options: {
        metadata: null,
        models: [],
      },
      selected: {
        models: [],
        filters: [],
        systemInstructions: "",
      },
    },
  },
});

// --------------- module definition ---------------

export default {
  namespaced: true,

  persist: [],

  state: createDefaultState,

  mutations: {
    HYDRATE(state, payload) {
      Object.assign(state, payload || {});
    },

    SET_ACTIVE_TAB(state, tab) {
      state.activeTab = tab;
    },

    SET_RESPONSE_PROVIDER(state, provider) {
      state.chat.responseProvider = provider || "aws";
      try { localStorage.setItem("responseProvider", provider); } catch { /* noop */ }
    },

    SET_CHAT_MESSAGES(state, messages) {
      state.chat.messages = Array.isArray(messages) ? messages : [];
    },

    ADD_MESSAGE(state, { prompt, responses = [], role }) {
      if (role === "user") {
        state.chat.messages.push({
          prompt,
          role,
          responses: [],
          timestamp: new Date().toISOString(),
        });
      } else if (role === "ai") {
        const last = state.chat.messages[state.chat.messages.length - 1];
        if (last && last.role === "user") {
          last.responses = Array.isArray(responses) ? responses : [responses];
        }
      }
    },

    APPEND_CHAT_MESSAGE(state, message) {
      if (!message) return;
      state.chat.messages.push(message);
    },

    SET_IS_GENERATING_RESPONSE(state, flag) {
      state.chat.isGeneratingResponse = !!flag;
    },

    SET_IS_FETCHING_OPTIONS(state, flag) {
      state.chat.isFetchingOptions = !!flag;
    },

    SET_CHAT_ERROR(state, error) {
      state.chat.error = error || null;
    },

    SET_SESSION_ID(state, { modelId, sessionId }) {
      state.chat.sessionIds = {
        ...state.chat.sessionIds,
        [modelId]: sessionId,
      };
    },

    SET_PROVIDER_SELECTED(state, { provider, selected }) {
      if (!provider || !state.providers[provider] || !selected) return;
      state.providers[provider].selected = {
        ...(state.providers[provider].selected || {}),
        ...selected,
      };
    },

    SET_PROVIDER_OPTIONS(state, { provider, options }) {
      if (!provider || !state.providers[provider] || !options) return;
      state.providers[provider].options = {
        ...(state.providers[provider].options || {}),
        ...options,
      };
    },

    SET_MODELS(state, models) {
      state.models = models || {};
    },

    RESET_PROVIDER_SESSIONS(state) {
      state.chat.sessionIds = {};
    },

    CLEAR_CHAT(state) {
      state.chat.messages = [];
      state.chat.sessionIds = {};
    },

    CLEAR_SESSIONS(state) {
      state.chat.sessionIds = {};
    },

    ADD_COURSE_TO_PROVIDER(state, { provider, course }) {
      if (!provider || !state.providers[provider] || !course) return;
      const key = provider === "pragya" ? "courseSection" : "courses";
      const list = state.providers[provider].options[key];
      if (Array.isArray(list) && !list.some((c) => c.value === course.value)) {
        list.push(course);
      }
    },

    RESET(state) {
      Object.assign(state, createDefaultState());
    },
  },

  actions: {
    setResponseProvider({ commit }, provider) {
      commit("SET_RESPONSE_PROVIDER", provider);
      commit("CLEAR_SESSIONS");
      commit("SET_IS_GENERATING_RESPONSE", false);
      commit("SET_CHAT_ERROR", null);
    },

    setProviderSelected({ commit }, payload) {
      commit("SET_PROVIDER_SELECTED", payload || {});
    },

    setProviderOptions({ commit }, payload) {
      commit("SET_PROVIDER_OPTIONS", payload || {});
    },

    resetProviderSessions({ commit, state }, provider) {
      const models = state.providers[provider]?.selected?.models || [];
      models.forEach((m) =>
        commit("SET_SESSION_ID", { modelId: m.id, sessionId: null }),
      );
    },

    // ---- Fetch provider options from real backends ----

    async fetchAwsOptions({ commit }) {
      commit("SET_IS_FETCHING_OPTIONS", true);
      commit("SET_CHAT_ERROR", null);
      try {
        const response = await AwsAiService.getAllFolders();
        const courses = mapFoldersToCourses(response.data.body?.folders);
        commit("SET_PROVIDER_OPTIONS", {
          provider: "aws",
          options: { courses },
        });
      } catch (error) {
        handleError(commit, error, "fetchAwsOptions");
      } finally {
        commit("SET_IS_FETCHING_OPTIONS", false);
      }
    },

    async fetchPragyaOptions({ commit, state, dispatch }) {
      commit("SET_IS_FETCHING_OPTIONS", true);
      commit("SET_CHAT_ERROR", null);
      try {
        const coursesRes = await PragyaAiService.getAllCourses();
        const courseSection = mapFoldersToCourses(coursesRes.data?.folders);

        if (state.providers.aws.options.courses?.length === 0) {
          await dispatch("fetchAwsOptions");
        }

        const s3Courses = state.providers.aws.options.courses;
        const mode = state.providers.pragya.selected.mode.value || "s3";
        const courses = mode === "s3" ? s3Courses : courseSection;

        commit("SET_PROVIDER_OPTIONS", {
          provider: "pragya",
          options: { courses, courseSection },
        });
      } catch (error) {
        handleError(commit, error, "fetchPragyaOptions");
      } finally {
        commit("SET_IS_FETCHING_OPTIONS", false);
      }
    },

    async fetchVertexOptions({ commit }) {
      commit("SET_IS_FETCHING_OPTIONS", true);
      commit("SET_CHAT_ERROR", null);
      try {
        const response = await VertexAiService.getMetadataConfig();
        const data = response.data || {};
        const metadata = { ...data };
        delete metadata.models;

        commit("SET_PROVIDER_OPTIONS", {
          provider: "vertex",
          options: { metadata },
        });
      } catch (error) {
        handleError(commit, error, "fetchVertexOptions");
      } finally {
        commit("SET_IS_FETCHING_OPTIONS", false);
      }
    },

    async syncChatModelCatalogFromServer({ commit, state }) {
      try {
        const response = await PragyaAiService.getChatModelCatalog();
        const { catalog, ollama = [] } = response.data || {};
        if (!catalog) {
          return;
        }
        const tag = (rows, provider, parent) =>
          (rows || []).map((m) => ({
            ...m,
            provider: m.provider || provider,
            parent: m.parent || parent,
          }));
        const pragyaModels = [
          ...tag(catalog.openai, "openai", "OpenAI"),
          ...tag(catalog.anthropic, "anthropic", "Anthropic"),
          ...tag(catalog.vertex, "vertex", "Google"),
          ...tag(ollama, "ollama", "Ollama"),
        ];
        commit("SET_PROVIDER_OPTIONS", {
          provider: "pragya",
          options: { models: pragyaModels },
        });

        const vertexOpts = state.providers.vertex?.options || {};
        commit("SET_PROVIDER_OPTIONS", {
          provider: "vertex",
          options: { ...vertexOpts, models: catalog.vertex || [] },
        });
      } catch (error) {
        console.error("syncChatModelCatalogFromServer:", error);
      }
    },

    async initializeProviders({ commit, dispatch, state }) {
      try {
        await Promise.all([
          dispatch("fetchAwsOptions").catch((e) =>
            console.warn("[initializeProviders] AWS:", e.message || e),
          ),
          dispatch("fetchPragyaOptions").catch((e) =>
            console.warn("[initializeProviders] Pragya:", e.message || e),
          ),
          dispatch("fetchVertexOptions").catch((e) =>
            console.warn("[initializeProviders] Vertex:", e.message || e),
          ),
        ]);
        await dispatch("syncChatModelCatalogFromServer");
        const flat = state.providers.pragya.options.models || [];
        const models = groupChatModelsByProvider(flat);
        commit("SET_MODELS", models);
      } catch (error) {
        console.error("Failed to initialize some providers:", error);
      } finally {
        commit("SET_CHAT_ERROR", null);
        commit("SET_IS_FETCHING_OPTIONS", false);
      }
    },

    // ---- Chat actions ----

    clearChat({ commit }) {
      commit("CLEAR_CHAT");
    },

    clearSessions({ commit }) {
      commit("CLEAR_SESSIONS");
    },

    setIsFetchingOptions({ commit }, flag) {
      commit("SET_IS_FETCHING_OPTIONS", flag);
    },

    addCourseToProvider({ commit }, payload) {
      const course =
        typeof payload?.course === "string"
          ? { value: payload.course, name: payload.course }
          : payload?.course;
      commit("ADD_COURSE_TO_PROVIDER", {
        provider: payload?.provider,
        course,
      });
    },

    clearError({ commit }) {
      commit("SET_CHAT_ERROR", null);
    },

    async generateResponse({ commit, state }, { prompt }) {
      if (!prompt) return;

      commit("SET_IS_GENERATING_RESPONSE", true);
      commit("SET_CHAT_ERROR", null);

      try {
        const provider = state.chat.responseProvider;
        const providerConfig = state.providers[provider];
        const selectedModels = providerConfig.selected.models || [];

        if (selectedModels.length === 0) {
          throw new Error(`No models selected for ${provider} provider`);
        }

        commit("ADD_MESSAGE", { prompt, role: "user" });

        const handler = providerHandlers[provider];
        if (!handler) throw new Error(`No handler for provider: ${provider}`);

        const responses = await Promise.all(
          selectedModels.map(async (model) => {
            const sessionId = state.chat.sessionIds[model.id] || null;
            try {
              const result = await handler(
                model,
                prompt,
                providerConfig.selected,
                sessionId,
              );

              if (result.sessionId) {
                commit("SET_SESSION_ID", {
                  modelId: model.id,
                  sessionId: result.sessionId,
                });
              }
              return result;
            } catch (modelError) {
              console.error(`Error with model ${model.name}:`, modelError);
              return {
                model,
                message: modelError.message || "Error generating response",
                resources: [],
                error: true,
              };
            }
          }),
        );

        commit("ADD_MESSAGE", {
          responses: responses.map(({ model, message, resources, sessionId }) => ({
            model,
            message,
            resources,
            sessionId,
          })),
          role: "ai",
        });
      } catch (error) {
        handleError(commit, error, "generateResponse");
      } finally {
        commit("SET_IS_GENERATING_RESPONSE", false);
      }
    },
  },

  getters: {
    modelsByProvider: (state) => (provider) =>
      state.providers[provider]?.options?.models || [],
    currentProvider: (state) => state.providers[state.chat.responseProvider],
    hasActiveSessions: (state) =>
      Object.keys(state.chat.sessionIds || {}).length > 0,
  },
};
